import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Select,
  MenuItem,
  Typography,
  Stack,
  Divider,
  Autocomplete,
  TextField,
  Chip,
  Box,
  Switch,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  useMediaQuery,
  useTheme,
  ListItemButton,
  ListItemIcon,
  alpha,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  PersonAdd as PersonAddIcon,
  Close,
} from "@mui/icons-material";
import { FolderPermissionDto, RoomMemberDto } from "./FileManager.tsx";
import { PaletteColor, PaletteColorHex } from "../types/PaletteColor.ts";

interface PermissionDialogProps {
  open: boolean;
  onClose: () => void;
  folderId: string;
  folderName: string;
  isRestricted: boolean;
  honbuId: number;
  roomId: number;
  palette: PaletteColor;
  onToggleRestriction: (folderId: string) => Promise<void>;
  onRefresh: () => void;
  showToast: (message: string, severity: "success" | "error") => void;
}

export const PermissionDialog: React.FC<PermissionDialogProps> = ({
  open,
  onClose,
  folderId,
  folderName,
  isRestricted,
  honbuId,
  roomId,
  palette,
  onToggleRestriction,
  onRefresh,
  showToast,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [permissions, setPermissions] = useState<FolderPermissionDto[]>([]);
  const [roomMembers, setRoomMembers] = useState<RoomMemberDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedMembersPC, setSelectedMembersPC] = useState<RoomMemberDto[]>(
    [],
  );
  const [selectedLevel, setSelectedLevel] = useState<number>(1);

  // スマホ用: メンバー追加ステップダイアログ
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);
  const [addStep, setAddStep] = useState<1 | 2>(1);
  const [selectedMembers, setSelectedMembers] = useState<RoomMemberDto[]>([]);
  const [bulkLevel, setBulkLevel] = useState<number>(1);

  const fetchPermissions = async () => {
    if (!isRestricted) return;
    setLoading(true);
    try {
      const res = await fetch(
        `/api/files/folders/${folderId}/permissions?honbuId=${honbuId}`,
      );
      if (res.ok) {
        const data = await res.json();
        setPermissions(data.permissions || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomMembers = async () => {
    try {
      const res = await fetch(`/api/files/room-members?roomId=${roomId}&honbuId=${honbuId}`);
      if (res.ok) {
        const data = await res.json();
        setRoomMembers(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (open) {
      fetchPermissions();
      fetchRoomMembers();
    }
  }, [open, isRestricted]);

  const handleAddPermission = async () => {
    if (selectedMembersPC.length === 0) return;
    try {
      for (const member of selectedMembersPC) {
        const res = await fetch(
          `/api/files/folders/${folderId}/permissions?honbuId=${honbuId}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              targetHonbuId: member.honbuId,
              accessLevel: selectedLevel,
            }),
          },
        );
        if (!res.ok) {
          showToast(await res.text(), "error");
          return;
        }
      }
      showToast(
        selectedMembersPC.length === 1
          ? "権限を追加しました"
          : `${selectedMembersPC.length}名の権限を追加しました`,
        "success",
      );
      setSelectedMembersPC([]);
      fetchPermissions();
    } catch (err) {
      console.error(err);
      showToast("通信エラーが発生しました", "error");
    }
  };

  const handleChangeLevel = async (targetHonbuId: number, newLevel: number) => {
    try {
      const res = await fetch(
        `/api/files/folders/${folderId}/permissions?honbuId=${honbuId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            targetHonbuId,
            accessLevel: newLevel,
          }),
        },
      );
      if (res.ok) {
        fetchPermissions();
      } else {
        showToast(await res.text(), "error");
      }
    } catch (err) {
      console.error(err);
      showToast("通信エラーが発生しました", "error");
    }
  };

  const handleRemovePermission = async (targetHonbuId: number) => {
    try {
      const res = await fetch(
        `/api/files/folders/${folderId}/permissions/${targetHonbuId}?honbuId=${honbuId}`,
        { method: "DELETE" },
      );
      if (res.ok) {
        showToast("権限を削除しました", "success");
        fetchPermissions();
      } else {
        showToast(await res.text(), "error");
      }
    } catch (err) {
      console.error(err);
      showToast("通信エラーが発生しました", "error");
    }
  };

  const handleToggle = async () => {
    await onToggleRestriction(folderId);
    onRefresh();
  };

  const handleOpenAddMemberDialog = () => {
    setSelectedMembers([]);
    setBulkLevel(1);
    setAddStep(1);
    setAddMemberDialogOpen(true);
  };

  const handleCloseAddMemberDialog = () => {
    setAddMemberDialogOpen(false);
    setSelectedMembers([]);
    setBulkLevel(1);
    setAddStep(1);
  };

  const handleToggleMemberSelection = (member: RoomMemberDto) => {
    setSelectedMembers((prev) =>
      prev.some((m) => m.honbuId === member.honbuId)
        ? prev.filter((m) => m.honbuId !== member.honbuId)
        : [...prev, member],
    );
  };

  const handleBulkAddPermission = async () => {
    if (selectedMembers.length === 0) return;
    try {
      for (const member of selectedMembers) {
        const res = await fetch(
          `/api/files/folders/${folderId}/permissions?honbuId=${honbuId}`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              targetHonbuId: member.honbuId,
              accessLevel: bulkLevel,
            }),
          },
        );
        if (!res.ok) {
          showToast(await res.text(), "error");
          return;
        }
      }
      showToast(`${selectedMembers.length}名の権限を追加しました`, "success");
      handleCloseAddMemberDialog();
      fetchPermissions();
    } catch (err) {
      console.error(err);
      showToast("通信エラーが発生しました", "error");
    }
  };

  // 既に権限が設定されているメンバーを除外
  const availableMembers = roomMembers.filter(
    (m) => !permissions.some((p) => p.honbuId === m.honbuId),
  );

  const accentColor = palette ? PaletteColorHex[palette] : "#1976d2";

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" slotProps={{ paper: { sx: { borderRadius: 2, overflow: "hidden" } } }}>
      <Box
        sx={{
          background: `linear-gradient(to right, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.7)})`,
          color: "#fff",
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "3px solid #fff",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          {isRestricted ? (
            <LockIcon sx={{ color: "warning.light" }} />
          ) : (
            <LockOpenIcon sx={{ color: "rgba(255,255,255,0.7)" }} />
          )}
          <Typography variant="h6" fontWeight="bold">
            アクセス制限 - {folderName}
          </Typography>
        </Stack>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
          <Close />
        </IconButton>
      </Box>
      <Box sx={{ height: 3, bgcolor: theme.palette.primary.main }} />
      <DialogContent>
        {/* 制限ON/OFFスイッチ */}
        <FormControlLabel
          control={
            <Switch
              checked={isRestricted}
              onChange={handleToggle}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": { color: accentColor },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: accentColor,
                },
              }}
            />
          }
          label={
            <Typography variant="body2">
              {isRestricted
                ? "制限ON（権限のある人のみアクセス可能）"
                : "制限OFF（全員がアクセス可能）"}
            </Typography>
          }
          sx={{ mb: 2 }}
        />

        {isRestricted && (
          <>
            <Divider sx={{ mb: 2 }} />

            {/* メンバー追加 */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              メンバーを追加
            </Typography>
            {isMobile ? (
              <Button
                variant="outlined"
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={handleOpenAddMemberDialog}
                disabled={availableMembers.length === 0}
                sx={{ mb: 2, borderColor: accentColor, color: accentColor }}
              >
                メンバーを追加
              </Button>
            ) : (
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1}>
                  <Autocomplete
                    multiple
                    size="small"
                    options={availableMembers}
                    getOptionLabel={(option) => option.name}
                    value={selectedMembersPC}
                    onChange={(_, value) => setSelectedMembersPC(value)}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="メンバーを検索..." />
                    )}
                    sx={{ flexGrow: 1 }}
                    noOptionsText="該当なし"
                    disableCloseOnSelect
                  />
                  <Select
                    size="small"
                    value={selectedLevel}
                    onChange={(e) =>
                      setSelectedLevel(e.target.value as number)
                    }
                    sx={{ minWidth: 100 }}
                  >
                    <MenuItem value={1}>閲覧者</MenuItem>
                    <MenuItem value={2}>編集者</MenuItem>
                  </Select>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleAddPermission}
                    disabled={selectedMembersPC.length === 0}
                    sx={{ bgcolor: accentColor, whiteSpace: "nowrap" }}
                  >
                    追加
                  </Button>
                </Stack>
              </Stack>
            )}

            <Divider sx={{ mb: 1 }} />

            {/* 権限一覧 */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              アクセス権限
            </Typography>
            {loading ? (
              <Typography variant="body2" color="text.secondary">
                Now Loading...
              </Typography>
            ) : permissions.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                権限が設定されていません
              </Typography>
            ) : (
              <List dense>
                {permissions.map((perm) => (
                  <ListItem key={perm.honbuId} sx={{ px: 0 }}>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2">{perm.name}</Typography>
                          {perm.honbuId === honbuId && (
                            <Chip
                              label="自分"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Stack>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Select
                          size="small"
                          value={perm.accessLevel}
                          onChange={(e) =>
                            handleChangeLevel(
                              perm.honbuId,
                              e.target.value as number,
                            )
                          }
                          variant="standard"
                          sx={{ fontSize: "0.85rem" }}
                        >
                          <MenuItem value={1}>閲覧者</MenuItem>
                          <MenuItem value={2}>編集者</MenuItem>
                        </Select>
                        <IconButton
                          size="small"
                          onClick={() => handleRemovePermission(perm.honbuId)}
                          color="error"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}

            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                閲覧者: フォルダ内のファイルの閲覧・ダウンロードのみ可能
              </Typography>
              <br />
              <Typography variant="caption" color="text.secondary">
                編集者:
                閲覧に加え、ファイルの移動・削除・名前変更、権限管理が可能
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>閉じる</Button>
      </DialogActions>

      {/* スマホ用: メンバー追加ステップダイアログ */}
      <Dialog
        open={addMemberDialogOpen}
        onClose={handleCloseAddMemberDialog}
        fullWidth
        maxWidth="sm"
        fullScreen={isMobile}
        slotProps={{ paper: { sx: { borderRadius: 2, overflow: "hidden" } } }}
      >
        <Box
          sx={{
            background: `linear-gradient(to right, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.7)})`,
            color: "#fff",
            px: 2,
            py: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "3px solid #fff",
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {addStep === 1 ? "メンバーを選択" : "権限を選択"}
          </Typography>
          <IconButton onClick={handleCloseAddMemberDialog} size="small" sx={{ color: "#fff" }}>
            <Close />
          </IconButton>
        </Box>
        <Box sx={{ height: 3, bgcolor: theme.palette.primary.main }} />
        <DialogContent dividers>
          {addStep === 1 ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                追加するメンバーを選んでください（複数選択可）
              </Typography>
              {availableMembers.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  追加できるメンバーがいません
                </Typography>
              ) : (
                <List dense disablePadding>
                  {availableMembers.map((member) => {
                    const checked = selectedMembers.some(
                      (m) => m.honbuId === member.honbuId,
                    );
                    return (
                      <ListItemButton
                        key={member.honbuId}
                        onClick={() => handleToggleMemberSelection(member)}
                        dense
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <Checkbox
                            edge="start"
                            checked={checked}
                            tabIndex={-1}
                            disableRipple
                            sx={{
                              "&.Mui-checked": { color: accentColor },
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary={member.name} />
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                選択中:{" "}
                <strong>{selectedMembers.map((m) => m.name).join("、")}</strong>
              </Typography>
              <FormControl>
                <FormLabel sx={{ mb: 1, color: "text.primary" }}>
                  権限を選んでください
                </FormLabel>
                <RadioGroup
                  value={bulkLevel}
                  onChange={(e) => setBulkLevel(Number(e.target.value))}
                >
                  <FormControlLabel
                    value={1}
                    control={
                      <Radio sx={{ "&.Mui-checked": { color: accentColor } }} />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">閲覧者</Typography>
                        <Typography variant="caption" color="text.secondary">
                          ファイルの閲覧・ダウンロードのみ可能
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value={2}
                    control={
                      <Radio sx={{ "&.Mui-checked": { color: accentColor } }} />
                    }
                    label={
                      <Box>
                        <Typography variant="body2">編集者</Typography>
                        <Typography variant="caption" color="text.secondary">
                          閲覧に加え、移動・削除・名前変更・権限管理が可能
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseAddMemberDialog}>キャンセル</Button>
          {addStep === 1 ? (
            <>
              {addStep === 1 && selectedMembers.length > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ flexGrow: 1, textAlign: "left" }}
                >
                  {selectedMembers.length}名選択中
                </Typography>
              )}
              <Button
                variant="contained"
                onClick={() => setAddStep(2)}
                disabled={selectedMembers.length === 0}
                sx={{ bgcolor: accentColor }}
              >
                次へ
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setAddStep(1)}>戻る</Button>
              <Button
                variant="contained"
                onClick={handleBulkAddPermission}
                sx={{ bgcolor: accentColor }}
              >
                決定
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
